<template>
  <div class="renew-page">
    <!-- Loader initial -->
    <div v-if="isLoadingDevis" class="renew-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Chargement de votre contrat...</p>
    </div>

    <!-- Erreur (lien invalide / contrat introuvable) -->
    <div v-else-if="loadError" class="renew-state renew-state--error">
      <i class="fas fa-exclamation-triangle"></i>
      <h2>Lien de renouvellement invalide</h2>
      <p>{{ loadError }}</p>
      <a href="/" class="cta-secondary">Retour à l'accueil</a>
    </div>

    <!-- VUE 2 - Confirmation succes -->
    <div v-else-if="paymentSuccess" class="renew-shell">
      <div class="confirm-card">
        <span v-for="i in 6" :key="`c${i}`" :class="`confetti confetti--${i}`"></span>
        <div class="check-circle"><i class="fas fa-check"></i></div>
        <h2>Assurance renouvelée</h2>
        <p class="subtitle">Merci pour votre confiance.</p>

        <ul class="recap">
          <li>
            <span class="lbl"><i class="fas fa-file-contract"></i> Contrat</span>
            <span class="val">{{ contractNumber }}</span>
          </li>
          <li v-if="avenantNumber">
            <span class="lbl"><i class="fas fa-hashtag"></i> Avenant</span>
            <span class="val">{{ avenantNumber }}</span>
          </li>
          <li>
            <span class="lbl"><i class="far fa-calendar"></i> Période</span>
            <span class="val">{{ formatDate(dateEffet) }} &rarr; {{ formatDate(dateEcheance) }}</span>
          </li>
          <li class="recap--prime">
            <span class="lbl"><i class="fas fa-check-circle"></i> Montant payé</span>
            <span class="val">{{ formatPrice(primeMontant) }} FCFA</span>
          </li>
        </ul>

        <div class="confirm-actions">
          <button class="confirm-btn confirm-btn--primary" @click="downloadAttestation">
            <i class="fas fa-download"></i> Télécharger mon attestation
          </button>
          <button class="confirm-btn confirm-btn--wa" @click="shareWhatsApp">
            <i class="fab fa-whatsapp"></i> Recevoir sur WhatsApp
          </button>
          <button class="confirm-btn confirm-btn--mail" @click="shareEmail">
            <i class="far fa-envelope"></i> Recevoir par email
          </button>
        </div>
      </div>
    </div>

    <!-- VUE 1 - Renouvellement (recap + paiement) -->
    <div v-else-if="newDevis" class="renew-shell">
      <div class="lead-hero">
        <h1>Renouvellement <span class="shadow-text">Auto</span></h1>
        <p>Plus Simple. Plus rapide.</p>
      </div>

      <div class="renew-card">
        <div class="renew-card__ids">
          <span class="renew-card__badge">
            <i class="fas fa-shield-alt"></i> Contrat {{ contractNumber }}
          </span>
          <span v-if="avenantNumber" class="renew-card__avenant">
            <i class="fas fa-hashtag"></i> Avenant {{ avenantNumber }}
          </span>
        </div>

        <div class="renew-section-title">Résumé</div>
        <ul class="recap">
          <li>
            <span class="lbl"><i class="fas fa-car-side"></i> Véhicule</span>
            <span class="val">{{ vehiculeLabel }}</span>
          </li>
          <li>
            <span class="lbl"><i class="fas fa-id-card"></i> Immatriculation</span>
            <span class="val">{{ immatriculation }}</span>
          </li>
          <li>
            <span class="lbl"><i class="fas fa-shield-alt"></i> Garantie</span>
            <span class="val">{{ garantieLabel }}</span>
          </li>
          <li>
            <span class="lbl"><i class="far fa-calendar"></i> Durée</span>
            <span class="val">{{ duree }} mois</span>
          </li>
          <li class="recap--prime">
            <span class="lbl"><i class="fas fa-tag"></i> Prime à payer</span>
            <span class="val">{{ formatPrice(primeMontant) }} FCFA</span>
          </li>
        </ul>

        <div class="renew-section-title">Moyen de paiement</div>
        <div class="pay-options">
          <div
            class="pay-option pay-option--om"
            :class="{ 'pay-option--selected': payMode === 'om' }"
            @click="payMode = 'om'"
          >
            <span v-if="favoritePayment === 'om'" class="pay-option__fav">&starf; Favori</span>
            <span class="pay-option__radio"></span>
            <img class="pay-option__logo" src="../../assets/img/logoPaiement/orange-money.png" alt="Orange Money" />
            <span class="pay-option__name">Orange Money</span>
          </div>
          <div
            class="pay-option pay-option--wave"
            :class="{ 'pay-option--selected': payMode === 'wave' }"
            @click="payMode = 'wave'"
          >
            <span v-if="favoritePayment === 'wave'" class="pay-option__fav">&starf; Favori</span>
            <span class="pay-option__radio"></span>
            <img class="pay-option__logo" src="../../assets/img/logoPaiement/wave.png" alt="Wave" />
            <span class="pay-option__name">Wave</span>
          </div>
        </div>

        <button class="cta-pay" :disabled="isProcessingPayment" @click="initiatePayment">
          <template v-if="isProcessingPayment">
            <i class="fas fa-spinner fa-spin"></i> Traitement...
          </template>
          <template v-else>
            <i class="fas fa-lock"></i> Payer {{ formatPrice(primeMontant) }} FCFA
          </template>
        </button>

        <router-link :to="modifierUrl" class="modifier-link">
          Autres : modifier devis et garanties
        </router-link>

        <div class="trust-line">
          <i class="fas fa-shield-alt"></i> Paiement 100% sécurisé &middot; Données chiffrées
        </div>
      </div>
    </div>

    <!-- Modal OM (numero + OTP) -->
    <div v-if="showOmModal" class="modal-overlay" @click.self="showOmModal = false">
      <div class="modal-content">
        <h3><i class="fas fa-mobile-alt"></i> Paiement Orange Money</h3>
        <p class="modal-hint">Composez <strong>#144#391#</strong> pour générer un code OTP.</p>
        <div class="form-group">
          <label>Numéro Orange Money</label>
          <input type="tel" v-model="omPhone" placeholder="77XXXXXXX" />
        </div>
        <div class="form-group">
          <label>Code OTP (6 chiffres)</label>
          <input type="text" v-model="omOtp" placeholder="123456" maxlength="6" />
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="showOmModal = false" :disabled="isProcessingPayment">Annuler</button>
          <button class="btn-primary" :disabled="!isOmFormValid || isProcessingPayment" @click="payWithOM">
            <i v-if="isProcessingPayment" class="fas fa-spinner fa-spin"></i>
            <span v-else>Valider {{ formatPrice(primeMontant) }} FCFA</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import Api from '../../services/Api';
import TiaKTiaKService from '../../services/TiaKTiaKService';
import wavepayment from '../../services/WaveServices';
import store from '../../store/store';
import {
  RC_VALUE,
  GARANTIES_AUTO,
  GARANTIES_MOTO,
  detectSelectedGarantiesAuto,
  detectSelectedGarantiesMoto,
} from './renewGaranties';

const ToastOk = { theme: 'toasted-primary', position: 'top-center', duration: 5000, type: 'success' };
const ToastErr = { theme: 'toasted-primary', position: 'top-center', duration: 5000, type: 'error' };

export default {
  name: 'AutoRenew',
  data() {
    return {
      newDevis: null,
      isLoadingDevis: true,
      loadError: null,
      duree: 12,
      dateEffet: null,
      dateEcheance: null,
      payMode: 'om',
      favoritePayment: null,
      isProcessingPayment: false,
      paymentSuccess: false,
      showOmModal: false,
      omPhone: '',
      omOtp: '',
      attestationLink: null
    };
  },

  computed: {
    contractNumber() {
      return this.newDevis ? this.newDevis.numeroDevis : '';
    },
    avenantNumber() {
      return this.newDevis ? (this.newDevis.last_avenant || '') : '';
    },
    primeMontant() {
      if (!this.newDevis) return 0;
      return Number(this.newDevis.prime_ttc || this.newDevis.primettc || 0);
    },
    vehiculeLabel() {
      const v = this.firstVehicule;
      if (!v) return '-';
      return [v.marque, v.model || v.modele].filter(Boolean).join(' ');
    },
    immatriculation() {
      const v = this.firstVehicule;
      return v ? (v.immatriculation || '-') : '-';
    },
    isMoto() {
      const v = this.firstVehicule;
      return !!(v && v.categorie === '2_roues');
    },
    garantieLabel() {
      const v = this.firstVehicule;
      if (!v || !Array.isArray(v.garanties) || !v.garanties.length) return '-';
      const source = this.isMoto ? GARANTIES_MOTO : GARANTIES_AUTO;
      const selected = this.isMoto
        ? detectSelectedGarantiesMoto(v.garanties)
        : detectSelectedGarantiesAuto(v.garanties);
      const labels = selected
        .map(val => (source.find(g => g.value === val) || {}).label)
        .filter(Boolean);
      return labels.length ? labels.join(', ') : '-';
    },
    modifierUrl() {
      const q = {};
      if (this.duree) q.duree = this.duree;
      const v = this.firstVehicule;
      if (v && Array.isArray(v.garanties) && v.garanties.length) {
        const selected = this.isMoto
          ? detectSelectedGarantiesMoto(v.garanties)
          : detectSelectedGarantiesAuto(v.garanties);
        q.garanties = selected.join(',');
      }
      return { path: `/n/${this.$route.params.id}/modifier`, query: q };
    },
    firstVehicule() {
      if (!this.newDevis || !Array.isArray(this.newDevis.vehicules) || !this.newDevis.vehicules.length) return null;
      return this.newDevis.vehicules[0];
    },
    isOmFormValid() {
      return /^\d{9}$/.test(this.omPhone) && /^\d{6}$/.test(this.omOtp);
    }
  },

  methods: {
    formatPrice(n) {
      const num = Number(n) || 0;
      return num.toLocaleString('fr-FR');
    },
    formatDate(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : '-';
    },

    async fetchDevis(code) {
      this.isLoadingDevis = true;
      this.loadError = null;
      try {
        const createdby = store.state && store.state.user ? store.state.user._id : undefined;
        const { data } = await Api().post('devis/getbyrenew', { code_renew: code, createdby });
        if (!data || data.message === 'non autorisation !' || !data.devis) {
          this.loadError = "Le lien n'est plus valide. Contactez Assuraf si besoin.";
          return;
        }
        this.newDevis = { type_avenant: 'RENOUVELLEMENT', ...data.devis };
        this.applyModifiersFromQuery();
        this.dateEffet = dayjs(this.newDevis.dateEcheance).add(1, 'day').format('YYYY-MM-DD');
        this.dateEcheance = dayjs(this.dateEffet).add(this.duree, 'month').add(-1, 'day').format('YYYY-MM-DD');
        this.favoritePayment = data.favorite_payment || null;
        if (this.favoritePayment) this.payMode = this.favoritePayment;
      } catch (err) {
        console.error('Erreur fetchDevis:', err);
        this.loadError = "Impossible de charger le contrat. Vérifiez votre lien.";
      } finally {
        this.isLoadingDevis = false;
      }
    },

    applyModifiersFromQuery() {
      const q = this.$route.query || {};
      const d = Number(q.duree);
      if ([3, 6, 12].includes(d)) this.duree = d;
      if (this.newDevis && Array.isArray(this.newDevis.vehicules) && this.newDevis.vehicules.length
        && typeof q.garanties === 'string' && q.garanties.length) {
        const isMotoDevis = this.newDevis.vehicules[0].categorie === '2_roues';
        const allowed = new Set((isMotoDevis ? GARANTIES_MOTO : GARANTIES_AUTO).map(g => g.value));
        const wanted = q.garanties.split(',').map(s => s.trim()).filter(Boolean).filter(g => allowed.has(g));
        if (wanted.length) {
          if (!wanted.includes(RC_VALUE)) wanted.unshift(RC_VALUE);
          const uniq = Array.from(new Set(wanted));
          this.newDevis.vehicules.forEach((v) => { v.garanties = uniq.slice(); });
        }
      }
      this.applySimulatedPrimeFromStorage();
    },
    applySimulatedPrimeFromStorage() {
      const q = this.$route.query || {};
      if (!q.duree && !q.garanties) return;
      try {
        const raw = sessionStorage.getItem(`renew_modif_${this.$route.params.id}`);
        if (!raw) return;
        const sim = JSON.parse(raw);
        const sameDuree = String(sim.duree) === String(q.duree);
        const sameGaranties = Array.isArray(sim.garanties) && typeof q.garanties === 'string'
          && sim.garanties.slice().sort().join(',') === q.garanties.split(',').map(s => s.trim()).sort().join(',');
        if (sameDuree && sameGaranties && this.newDevis && typeof sim.prime_ttc === 'number') {
          this.newDevis.prime_ttc = sim.prime_ttc;
          if (typeof sim.prime_nette === 'number') this.newDevis.prime_nette = sim.prime_nette;
          if (typeof sim.taxe === 'number') this.newDevis.taxe = sim.taxe;
          if (typeof sim.accessoires === 'number') this.newDevis.accessoires = sim.accessoires;
        }
      } catch (e) { /* ignore corrupt storage */ }
    },

    async createAvenant() {
      if (!this.newDevis) throw new Error('Contrat non charge');
      const payload = {
        numero_police: this.newDevis.numeroDevis,
        userid: this.newDevis.userid,
        dateEffet: dayjs(this.dateEffet).format('YYYY-MM-DD'),
        dateEcheance: dayjs(this.dateEcheance).format('YYYY-MM-DD'),
        createur: this.newDevis.userid,
        branche: 'Automobile',
        duree: this.duree,
        dateSouscription: dayjs(new Date()),
        vehicule: this.newDevis.vehicules,
        assureurs: this.newDevis.partenaireid ? [this.newDevis.partenaireid.societe] : [],
        proprietaire: this.newDevis.proprietaire,
        plateforme: 'Front',
        type_avenant: 'RENOUVELLEMENT',
        courtier: null
      };
      const { data } = await Api().post('avenant/create/renew', payload);
      if (!data || data.message !== 'Devis modifié !') {
        throw new Error((data && data.message) || 'Erreur creation avenant');
      }
      return data;
    },

    initiatePayment() {
      if (this.payMode === 'om') {
        this.showOmModal = true;
      } else if (this.payMode === 'wave') {
        this.payWithWave();
      }
    },

    async payWithOM() {
      if (!this.isOmFormValid) return;
      this.isProcessingPayment = true;
      try {
        const avenant = await this.createAvenant();
        const numeroDevis = (avenant.devis && avenant.devis.numeroDevis) || this.newDevis.numeroDevis;
        const resp = await TiaKTiaKService.payWithOrangeMoneyTiaTchak({
          montant: this.primeMontant,
          telephone: this.omPhone,
          otp: this.omOtp,
          numeroDevis
        });
        if (resp && (resp.ok || resp.status === 'success' || resp.message === 'success')) {
          this.showOmModal = false;
          this.onPaymentSuccess(avenant);
        } else {
          this.$toasted.show((resp && resp.message) || 'Paiement refusé', ToastErr);
        }
      } catch (err) {
        console.error('Erreur paiement OM:', err);
        const msg = (err.response && err.response.data && err.response.data.message) || 'Échec du paiement Orange Money';
        this.$toasted.show(msg, ToastErr);
      } finally {
        this.isProcessingPayment = false;
      }
    },

    async payWithWave() {
      this.isProcessingPayment = true;
      try {
        const avenant = await this.createAvenant();
        const numeroDevis = (avenant.devis && avenant.devis.numeroDevis) || this.newDevis.numeroDevis;
        wavepayment(this.primeMontant, numeroDevis);
      } catch (err) {
        console.error('Erreur paiement Wave:', err);
        this.$toasted.show('Échec du paiement Wave', ToastErr);
      } finally {
        this.isProcessingPayment = false;
      }
    },

    onPaymentSuccess(avenant) {
      this.paymentSuccess = true;
      if (avenant && avenant.devis) {
        if (avenant.devis.numeroDevis) this.newDevis.numeroDevis = avenant.devis.numeroDevis;
        if (avenant.devis._id) this.attestationLink = avenant.devis._id;
      }
      this.$toasted.show('Renouvellement validé !', ToastOk);
    },

    async downloadAttestation() {
      const id = this.attestationLink || (this.newDevis && this.newDevis._id);
      if (!id) {
        this.$toasted.show('Attestation indisponible pour le moment.', ToastErr);
        return;
      }
      try {
        const { data } = await Api().get(`contrat/attestation/download/${id}`);
        if (data && data.lien) {
          window.open(data.lien, '_blank');
        } else {
          this.$toasted.show("Lien attestation introuvable.", ToastErr);
        }
      } catch (err) {
        console.error('Erreur attestation:', err);
        this.$toasted.show("Erreur de téléchargement.", ToastErr);
      }
    },

    shareWhatsApp() {
      const text = `Mon attestation Assuraf - Contrat ${this.contractNumber}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    },

    shareEmail() {
      const subject = `Attestation Assuraf - Contrat ${this.contractNumber}`;
      const body = `Bonjour,\nVoici les détails de votre renouvellement :\nContrat : ${this.contractNumber}\nPériode : ${this.formatDate(this.dateEffet)} au ${this.formatDate(this.dateEcheance)}\nMontant : ${this.formatPrice(this.primeMontant)} FCFA`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  },

  mounted() {
    window.scrollTo(0, 0);
    const code = this.$route.params.id;
    if (code) {
      this.fetchDevis(code);
    } else {
      this.isLoadingDevis = false;
      this.loadError = 'Lien de renouvellement manquant.';
    }
  }
};
</script>

<style scoped>
.renew-page {
  background: linear-gradient(180deg, #ffa50992 0%, #e0f7fa 30%, #ffffff 40%, #ffffff 100%);
  min-height: 100vh;
  padding: 100px 16px 60px;
  font-family: 'Lato', 'Roboto', sans-serif;
  color: #2b2b2b;
}

.renew-shell {
  max-width: 720px;
  margin: 0 auto;
}

.renew-state {
  max-width: 480px;
  margin: 80px auto;
  text-align: center;
  background: #fff;
  padding: 36px 24px;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.renew-state i {
  font-size: 42px;
  color: #ffa409;
  margin-bottom: 14px;
}
.renew-state--error i { color: #dc2626; }
.renew-state h2 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
}
.renew-state p {
  margin: 0 0 18px;
  color: #6b7280;
  font-size: 14px;
}
.cta-secondary {
  display: inline-block;
  background: #ffa409;
  color: #fff;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 700;
}

.lead-hero {
  text-align: center;
  padding: 20px 16px 8px;
}
.lead-hero h1 {
  font-size: 34px;
  font-weight: 800;
  margin: 0 0 6px;
}
.lead-hero h1 .shadow-text {
  color: #ffa409;
  text-shadow: 0 0 2px #fff, 0 1px 2px rgba(0, 0, 0, 0.3);
}
.lead-hero p {
  margin: 0;
  color: #191919;
  font-size: 16px;
}

.renew-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  margin-top: 24px;
  padding: 28px;
  position: relative;
}
.renew-card__ids {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 18px;
}

.renew-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0d4e78;
  color: #fff;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.renew-card__avenant {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #0d4e78;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
}

.renew-section-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #6b7280;
  font-weight: 700;
  margin: 14px 0 10px;
}

.recap {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
  overflow: hidden;
}
.recap li {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 13px;
  border-bottom: 1px solid #f3f4f6;
}
.recap li:last-child { border-bottom: none; }
.recap .lbl {
  color: #6b7280;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.recap .lbl i { color: #ffa409; width: 16px; text-align: center; }
.recap .val {
  color: #2b2b2b;
  font-weight: 700;
  text-align: right;
}
.recap li.recap--prime { background: linear-gradient(90deg, #fff7ed, #ffffff); }
.recap li.recap--prime .val {
  color: #ffa409;
  font-size: 18px;
}

.pay-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}
.pay-option {
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
  position: relative;
}
.pay-option__logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}
.pay-option__name {
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
}
.pay-option__radio {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  background: #fff;
}
.pay-option--selected {
  border-color: #ffa409;
  background: linear-gradient(180deg, #fff7ed, #ffffff);
  box-shadow: 0 4px 12px rgba(255, 164, 9, 0.15);
}
.pay-option--selected .pay-option__radio {
  border-color: #ffa409;
  background: #ffa409;
  box-shadow: inset 0 0 0 3px #fff;
}
.pay-option__fav {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #fde7c0;
  color: #b15a00;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.cta-pay {
  width: 100%;
  background: linear-gradient(90deg, #ffa409, #ff8a00);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 6px 16px rgba(255, 164, 9, 0.35);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 14px;
}
.cta-pay:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modifier-link {
  display: block;
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #ffa409;
  text-decoration: underline;
}
.modifier-link:hover { color: #b8790a; }

.trust-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.trust-line i { color: #16a34a; }

/* Confirmation card */
.confirm-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
  margin: 40px auto 0;
  max-width: 580px;
  padding: 36px 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
}
.confirm-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: linear-gradient(90deg, #16a34a, #22c55e);
}
.check-circle {
  width: 78px;
  height: 78px;
  background: linear-gradient(135deg, #16a34a, #22c55e);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin: 0 auto 18px;
  box-shadow: 0 8px 24px rgba(22, 163, 74, 0.4);
}
.confirm-card h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 800;
}
.confirm-card .subtitle {
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 24px;
}

.confetti {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.confetti--1 { background: #ffa409; top: 30px; left: 15%; transform: rotate(45deg); }
.confetti--2 { background: #16a34a; top: 50px; left: 30%; transform: rotate(20deg); }
.confetti--3 { background: #0d4e78; top: 20px; left: 70%; transform: rotate(60deg); }
.confetti--4 { background: #ec4899; top: 80px; left: 8%; transform: rotate(15deg); }
.confetti--5 { background: #8b5cf6; top: 40px; left: 88%; transform: rotate(-25deg); }
.confetti--6 { background: #fbbf24; top: 110px; left: 55%; transform: rotate(-45deg); }

.confirm-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 24px;
}
.confirm-btn {
  border-radius: 10px;
  padding: 13px;
  font-size: 13px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.confirm-btn--primary {
  background: #ffa409;
  color: #fff;
  box-shadow: 0 4px 12px rgba(255, 164, 9, 0.35);
}
.confirm-btn--wa {
  background: #fff;
  color: #25d366;
  border: 2px solid #25d366;
}
.confirm-btn--mail {
  background: #fff;
  color: #0d4e78;
  border: 2px solid #0d4e78;
}

/* Modal OM */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 16px;
}
.modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
}
.modal-content h3 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 800;
  color: #2b2b2b;
}
.modal-content h3 i { color: #ff7900; margin-right: 6px; }
.modal-hint {
  margin: 0 0 16px;
  font-size: 12px;
  color: #6b7280;
}
.form-group {
  margin-bottom: 14px;
}
.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}
.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
}
.form-group input:focus {
  outline: none;
  border-color: #ffa409;
  box-shadow: 0 0 0 3px rgba(255, 164, 9, 0.15);
}
.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
}
.btn-secondary,
.btn-primary {
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  border: none;
}
.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}
.btn-primary {
  background: #ffa409;
  color: #fff;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .renew-page { padding: 80px 12px 60px; }
  .lead-hero h1 { font-size: 24px; }
  .lead-hero p { font-size: 14px; }
  .renew-card { padding: 20px; }
  .confirm-card { padding: 28px 18px; }
  .check-circle { width: 64px; height: 64px; font-size: 28px; }
  .confirm-card h2 { font-size: 20px; }
}
</style>
